/** Persist Voice Nav dest-dock pill order (Forms / Operators / Delivery / Post + rail tools). */

/**
 * v5: Android Telegram Mini App migration — pin AR View (`compass`) early in the
 * horizontal dest-dock so it is not scrolled off-screen on narrow phones, and
 * never leave it in dest-dock hidden. Prior key was zwn-dest-dock-order-v4.
 * Merge also repositions compass when missing or buried past DEST_DOCK_COMPASS_MAX_INDEX.
 *
 * Keep the same localStorage key when only the *default* order changes so users who
 * already rearranged (or who got an auto-persisted order) keep their custom order.
 * New installs / cleared `zwn-dest-dock-order-v5` get DEFAULT_DEST_DOCK_ORDER.
 */
export const DEST_DOCK_ORDER_KEY = 'zwn-dest-dock-order-v5';
export const DEST_DOCK_HIDDEN_KEY = 'zwn-dest-dock-hidden-v1';
export const DEST_DOCK_PAINT_ID = 'paint';
export const DEST_DOCK_BUG_ID = 'bug';

/**
 * Default horizontal dest-dock order (left → right) for new installs / cleared order.
 * Labels: Forms, Favorites, Facilities (`search`), 2D Map, AR View (`compass`),
 * Browser (`api-browser`), Worklist, Services (`delivery`), Doppler, News, Post,
 * Company, Calculations. Desk pills lead the row (see DEST_DOCK_DESK_IDS); native API pad (`api`)
 * stays ahead of Forms when present. Other rail tools / Theme / Report append after via merge.
 * Verify + Background are not dest-dock pills: they moved into Office Desk (header M3 icons).
 */
export const DEFAULT_DEST_DOCK_ORDER: readonly string[] = [
  // Desk pills lead, with Safety between Contractor and Office.
  'operators',
  'financeDesk',
  'contractors',
  'safety',
  'office',
  'fieldDesk',
  'fleetDesk',
  'partsDesk',
  'api',
  'forms',
  'favorites',
  'search',
  'map',
  'compass',
  'ufo',
  'api-browser',
  'workList',
  'delivery',
  'doppler',
  'news',
  'post',
  'company',
  'calculations',
  'learn',
  'archived',
  'roadHazard',
  'cameraReports',
  DEST_DOCK_PAINT_ID,
  DEST_DOCK_BUG_ID,
];

/**
 * Desk pills, in the order they lead the dock (Cipher: desks first on Voice Nav).
 * Operator Desk = `operators` (opens OperatorDeskHub), Contractor Desk = `contractors`,
 * Field Desk = `fieldDesk`, Office Desk = `office`, Fleet Desk = `fleetDesk`, Parts Desk = `partsDesk`.
 * Order: Operator → Contractor → Office → Field → Fleet → Parts.
 * Services (`delivery`) is not a desk and keeps its place.
 */
export const DEST_DOCK_DESK_IDS = ['operators', 'financeDesk', 'contractors', 'office', 'fieldDesk', 'fleetDesk', 'partsDesk'] as const;

export function isDestDockDeskId(id: string): boolean {
  return (DEST_DOCK_DESK_IDS as readonly string[]).includes(id);
}

/** Lead with desks and place visible Safety between Contractor and Office. */
export function pinDeskPillsFirst(ids: readonly string[]): string[] {
  const desks: string[] = DEST_DOCK_DESK_IDS.filter((id) => ids.includes(id));
  const contractorAt = desks.indexOf('contractors');
  const officeAt = desks.indexOf('office');
  // Field-only profiles retain their own response-tool order.
  if (ids.includes('safety') && (contractorAt !== -1 || officeAt !== -1)) {
    desks.splice(contractorAt !== -1 ? contractorAt + 1 : officeAt, 0, 'safety');
  }
  return [...desks, ...ids.filter((id) => !desks.includes(id))];
}

/** How many desk pills sit at the very start of the row. */
export function leadingDeskPillCount(ids: readonly string[]): number {
  let count = 0;
  while (count < ids.length && isDestDockDeskId(ids[count])) count += 1;
  return count;
}

/**
 * `zwn-dest-dock-user-arranged-v1` = '1' once the user drag-reorders the dock.
 * Then their saved order is respected exactly (desks are not pulled to the front).
 * Applying a dock profile clears it (profile order = desks first).
 */
export const DEST_DOCK_USER_ARRANGED_KEY = 'zwn-dest-dock-user-arranged-v1';

export function readDestDockUserArranged(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DEST_DOCK_USER_ARRANGED_KEY) === '1';
  } catch {
    return false;
  }
}

export function writeDestDockUserArranged(on: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (on) window.localStorage.setItem(DEST_DOCK_USER_ARRANGED_KEY, '1');
    else window.localStorage.removeItem(DEST_DOCK_USER_ARRANGED_KEY);
  } catch {
    // Quota / private mode — arrangement still applies for this session.
  }
}

export const DEST_DOCK_CORE_IDS = ['api', 'forms', 'api-browser', 'fieldDesk', 'fleetDesk', 'partsDesk', 'contractors', 'office', 'operators', 'financeDesk', 'delivery', 'doppler', 'safety', 'news', 'post', 'company', 'calculations', 'learn', 'archived'] as const;
export type DestDockCoreId = (typeof DEST_DOCK_CORE_IDS)[number];

/** All tools, including AR View, may be explicitly archived. */
export const DEST_DOCK_ALWAYS_VISIBLE_IDS: readonly string[] = [];

/**
 * If `compass` is buried past this index after merge, reposition it.
 * Stale Android localStorage orders often put it after workList/roadHazard clutter.
 */
export const DEST_DOCK_COMPASS_MAX_INDEX = 7;

export function isDestDockCoreId(id: string): boolean {
  return (DEST_DOCK_CORE_IDS as readonly string[]).includes(id);
}

export function isDestDockPillId(id: string): boolean {
  return isDestDockCoreId(id) || id === DEST_DOCK_PAINT_ID || id === DEST_DOCK_BUG_ID;
}

export function isDestDockAlwaysVisibleId(id: string): boolean {
  return (DEST_DOCK_ALWAYS_VISIBLE_IDS as readonly string[]).includes(id);
}

/** Preserve explicit archive choices across reloads and profile changes. */
export function sanitizeDestDockHidden(ids: readonly string[]): string[] {
  return ids.filter((id) => !isDestDockAlwaysVisibleId(id));
}

/** Preferred insert index for compass among `withoutCompass` ids. */
export function preferredCompassDestDockIndex(withoutCompass: readonly string[]): number {
  const mapIdx = withoutCompass.indexOf('map');
  if (mapIdx !== -1) return mapIdx + 1;
  const favIdx = withoutCompass.indexOf('favorites');
  if (favIdx !== -1) return favIdx + 1;
  let lastCore = -1;
  for (let i = 0; i < withoutCompass.length; i += 1) {
    if (isDestDockCoreId(withoutCompass[i])) lastCore = i;
  }
  return lastCore !== -1 ? lastCore + 1 : Math.min(3, withoutCompass.length);
}

/** Keep unarchived AR in the first dock viewport, even when All has many desks.
 * Visual order only; the user's persisted order remains intact. */
export function promoteCompassForDockViewport(ids: string[]): string[] {
  const index = ids.indexOf('compass');
  if (index === -1) return ids;
  const next = ids.filter((id) => id !== 'compass');
  const insertAt = 0; // Keep AR within the first dock viewport, including All profiles.
  if (index === insertAt) return ids;
  next.splice(insertAt, 0, 'compass');
  return next;
}

export function pinCompassInDestDockOrder(ids: string[]): string[] {
  if (!ids.includes('compass')) return ids;

  const current = ids.indexOf('compass');
  const without = ids.filter((id) => id !== 'compass');
  const insertAt = preferredCompassDestDockIndex(without);
  const mapInFull = ids.indexOf('map');
  const alreadyAfterMap = mapInFull !== -1 && current === mapInFull + 1;
  const alreadyEarly = current !== -1 && current <= DEST_DOCK_COMPASS_MAX_INDEX;

  // Keep placement when already immediately after map, or early at preferred slot when map is absent.
  if (alreadyAfterMap) return ids;
  if (mapInFull === -1 && alreadyEarly && current === insertAt) return ids;

  const next = [...without];
  next.splice(insertAt, 0, 'compass');
  return next;
}

export function readDestDockOrder(): string[] | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(DEST_DOCK_ORDER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((id) => typeof id !== 'string')) return null;
    return parsed as string[];
  } catch {
    return null;
  }
}

export function writeDestDockOrder(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEST_DOCK_ORDER_KEY, JSON.stringify(ids));
  } catch {
    // Quota / private mode — order still applies for this session.
  }
}

export function readDestDockHidden(): string[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(DEST_DOCK_HIDDEN_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((id) => typeof id !== 'string')) return [];
    const cleaned = sanitizeDestDockHidden(parsed as string[]);
    // Persist any future always-visible tool policy without dropping archive choices.
    if (cleaned.length !== (parsed as string[]).length) {
      writeDestDockHidden(cleaned);
    }
    return cleaned;
  } catch {
    return [];
  }
}

export function writeDestDockHidden(ids: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(DEST_DOCK_HIDDEN_KEY, JSON.stringify(sanitizeDestDockHidden(ids)));
  } catch {
    // Quota / private mode — hidden set still applies for this session.
  }
}

/** Insert by DEFAULT_DEST_DOCK_ORDER neighbor when known; never reshuffle pills already present. */
function insertNewDestDockId(ids: string[], id: string): void {
  const preferred = DEFAULT_DEST_DOCK_ORDER.indexOf(id);
  if (preferred !== -1) {
    let insertAt = 0;
    for (let i = 0; i < ids.length; i += 1) {
      const pos = DEFAULT_DEST_DOCK_ORDER.indexOf(ids[i]);
      if (pos !== -1 && pos < preferred) insertAt = i + 1;
    }
    ids.splice(insertAt, 0, id);
    return;
  }
  if (id === 'ufo') {
    const arIndex = ids.indexOf('compass');
    ids.splice(arIndex < 0 ? ids.length : arIndex + 1, 0, id);
    return;
  }
  if (id === 'compass') {
    ids.splice(preferredCompassDestDockIndex(ids), 0, id);
    return;
  }
  ids.push(id);
}


/**
 * @deprecated No longer applied by mergeDestDockOrder — desk pills lead the row
 * instead (pinDeskPillsFirst) and Services keeps its own place. Kept for callers/tests.
 * Keep Contractor Desk immediately left of Operators on the voice-nav dest dock.
 * When Operators is absent, sit immediately left of Services (`delivery`).
 */
export function pinContractorsLeftOfOperators(ids: string[]): string[] {
  const contractorIdx = ids.indexOf('contractors');
  if (contractorIdx === -1) return ids;
  const opIdx = ids.indexOf('operators');
  const anchorId = opIdx !== -1 ? 'operators' : 'delivery';
  const anchorIdx = ids.indexOf(anchorId);
  if (anchorIdx === -1) return ids;
  if (contractorIdx === anchorIdx - 1) return ids;
  const without = ids.filter((id) => id !== 'contractors');
  const insertAt = without.indexOf(anchorId);
  if (insertAt === -1) return ids;
  const next = [...without];
  next.splice(insertAt, 0, 'contractors');
  return next;
}

/**
 * @deprecated No longer applied by mergeDestDockOrder (see pinDeskPillsFirst).
 * Keep Field Desk (field hands) immediately left of Contractor Desk when both
 * pills are on the dock. Without Contractor Desk (Field profile) it stays put.
 */
export function pinFieldDeskLeftOfContractors(ids: string[]): string[] {
  const fieldIdx = ids.indexOf('fieldDesk');
  const contractorIdx = ids.indexOf('contractors');
  if (fieldIdx === -1 || contractorIdx === -1) return ids;
  if (fieldIdx === contractorIdx - 1) return ids;
  const without = ids.filter((id) => id !== 'fieldDesk');
  const insertAt = without.indexOf('contractors');
  const next = [...without];
  next.splice(insertAt, 0, 'fieldDesk');
  return next;
}

/**
 * @deprecated No longer applied by mergeDestDockOrder — Operators (Operator Desk)
 * leads with the other desk pills; Services is decoupled and keeps its place.
 * Keep Operators immediately left of Services (`delivery`) on the voice-nav dest dock.
 * Saved localStorage orders (and stale defaults) often put Operators after News;
 * production feedback requires Operators → Services adjacency.
 */
export function pinOperatorsLeftOfServices(ids: string[]): string[] {
  const opIdx = ids.indexOf('operators');
  const svcIdx = ids.indexOf('delivery');
  if (opIdx === -1 || svcIdx === -1) return ids;
  if (opIdx === svcIdx - 1) return ids;
  const without = ids.filter((id) => id !== 'operators');
  const deliveryAt = without.indexOf('delivery');
  if (deliveryAt === -1) return ids;
  const next = [...without];
  next.splice(deliveryAt, 0, 'operators');
  return next;
}

/**
 * Visible dest-dock order. `desksFirst` (default) pulls the visible desk pills to the
 * front; pass `false` for a user drag-arranged order so it is respected exactly.
 */
export function mergeDestDockOrder(
  available: string[],
  saved: string[] | null,
  hidden: readonly string[] | null = null,
  options: { desksFirst?: boolean; preserveCompassPosition?: boolean } = {},
): string[] {
  const avail = new Set(available);
  // Explicitly archived tools stay hidden.
  const hide = new Set(sanitizeDestDockHidden(hidden || []));
  const out: string[] = [];
  if (saved && saved.length) {
    for (const id of saved) {
      if (avail.has(id) && !hide.has(id) && !out.includes(id)) out.push(id);
    }
  } else {
    for (const id of DEFAULT_DEST_DOCK_ORDER) {
      if (avail.has(id) && !hide.has(id) && !out.includes(id)) out.push(id);
    }
  }
  for (const id of available) {
    if (hide.has(id) || out.includes(id)) continue;
    insertNewDestDockId(out, id);
  }
  const pinned = options.preserveCompassPosition ? out : pinCompassInDestDockOrder(out);
  return options.desksFirst === false ? pinned : pinDeskPillsFirst(pinned);
}

export function moveDockId(ids: string[], id: string, toIndex: number): string[] {
  const next = ids.filter((item) => item !== id);
  const index = Math.max(0, Math.min(toIndex, next.length));
  next.splice(index, 0, id);
  return next;
}
